import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import stateList from '../../../components/common/states';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const validateField = (name, value) => {
  let error = '';
  if (name === 'bank' && value && value.length > 100) error = 'Bank cannot exceed 100 characters';
  if (name === 'fax_no' && value && value.length > 50) error = 'Fax No cannot exceed 50 characters';
  if (name === 'website' && value && value.length > 120) error = 'Website cannot exceed 120 characters';
  else if (name === 'website' && value && !/^((https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?)$/.test(value)) error = 'Enter a valid website (e.g. www.example.com)';
  if (name === 'parent_company' && value && value.length > 120) error = 'Parent Company cannot exceed 120 characters';
  if (name === 'currency' && value && value.length > 20) error = 'Currency cannot exceed 20 characters';
  if (name === 'account_no' && value && value.length > 50) error = 'Account No cannot exceed 50 characters';
  if (name === 'swift_code' && value && value.length > 50) error = 'SWIFT Code cannot exceed 50 characters';
  if (name === 'ad_code' && value && value.length > 30) error = 'AD Code cannot exceed 30 characters';
  if (name === 'iec_code' && value && value.length > 30) error = 'IEC Code cannot exceed 30 characters';
  if (name === 'gst_no' && value && value.length > 30) error = 'GST No cannot exceed 30 characters';
  if (name === 'fssai_no' && value && value.length > 30) error = 'FSSAI No cannot exceed 30 characters';
  if (name === 'address' && value && value.length > 255) error = 'Address cannot exceed 255 characters';
  if (name === 'pan_no' && value && value.length > 100) error = 'PAN No cannot exceed 100 characters';
  if (name === 'pincode' && value && value.length > 45) error = 'Pincode cannot exceed 45 characters';
  if (name === 'state' && value && value.length > 100) error = 'State cannot exceed 100 characters';
  if (name === 'state_code' && value && value.length > 10) error = 'State Code cannot exceed 10 characters';
  return error;
};

const validateForm = (formData) => {
  const errors = {};
  Object.keys(formData).forEach(field => {
    if (formData[field]) {
      const error = validateField(field, formData[field]);
      if (error) errors[field] = error;
    }
  });
  return errors;
};

const SupplierEditForm = () => {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const fetchSupplier = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/supplier-get?page=1&limit=1000&search=`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch supplier');
      const data = await res.json();
      const foundSupplier = data.data.find(s => s.supplier_id === parseInt(supplierId));
      
      if (!foundSupplier) {
        toast.error('Supplier not found');
        navigate('/supplier');
        return;
      }

      setSupplier(foundSupplier);
      
      // Include fields that can be edited (empty fields + state fields for flexibility)
      const emptyFields = {};
      Object.keys(foundSupplier).forEach(key => {
        // Include empty fields and state fields (even if they have values, allow editing)
        if (!foundSupplier[key] || foundSupplier[key] === '' || foundSupplier[key] === null || 
            key === 'state' || key === 'state_code') {
          emptyFields[key] = foundSupplier[key] || '';
        }
      });
      
      setFormData(emptyFields);
      setFetching(false);
    } catch (error) {
      toast.error('Failed to load supplier data');
      navigate('/supplier');
    }
  }, [supplierId, navigate]);

  useEffect(() => {
    fetchSupplier();
  }, [fetchSupplier]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      toast.error(error);
    }
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    const matched = stateList.find(s => s.name === selectedState);
    setFormData(prev => ({
      ...prev,
      state: selectedState,
      state_code: matched ? matched.code : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((msg) => {
        if (msg) toast.error(msg);
      });
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/supplier-put`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          supplier_id: parseInt(supplierId),
          updateData: formData
        }),
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success('Supplier updated successfully!');
        navigate('/supplier');
      } else {
        toast.error(data.error || 'Update failed');
      }
    } catch (err) {
      toast.error('Network error');
    }
    setLoading(false);
  };

  if (fetching) {
    return (
      <div className="max-w-8xl mx-auto p-4 bg-white">
        <div className="text-center text-blue-600 py-6 text-[13px]">Loading supplier data...</div>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="max-w-8xl mx-auto p-4 bg-white">
        <div className="text-center text-red-600 py-6 text-[13px]">Supplier not found</div>
      </div>
    );
  }

  // Check if there are any empty fields to edit
  const hasEmptyFields = Object.keys(formData).length > 0;

  if (!hasEmptyFields) {
    return (
      <div className="max-w-8xl mx-auto p-4 bg-white">
        <div className="flex justify-between items-center border-b pb-3 mb-5">
          <h2 className="text-[15px] font-medium text-gray-800">Edit Supplier</h2>
          <button
            type="button"
            onClick={() => navigate('/supplier')}
            className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-3 py-1 text-[12px] rounded font-normal hover:bg-[#166534] hover:text-white hover:border-[#166534] transition"
          >
            <FaArrowLeft />
            Back to List
          </button>
        </div>
        <div className="text-center text-gray-600 py-6 text-[13px]">
          All fields for this supplier are already filled. No updates needed.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-8xl mx-auto p-4 bg-white">
      <ToastContainer />
      <div className="flex justify-between items-center border-b pb-3 mb-5">
        <h2 className="text-[15px] font-medium text-gray-800">Edit Supplier - {supplier.name}</h2>
        <button
          type="button"
          onClick={() => navigate('/supplier')}
          className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-3 py-1 text-[12px] rounded font-normal hover:bg-[#166534] hover:text-white hover:border-[#166534] transition"
        >
          <FaArrowLeft />
          Back to List
        </button>
      </div>

      {/* Current Supplier Info */}
     

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {formData.bank !== undefined && (
            <div>
              <label className="block text-[13px] text-gray-700 mb-1">Bank</label>
              <input type="text" name="bank" value={formData.bank} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={100} />
            </div>
          )}
          {formData.fax_no !== undefined && (
            <div>
              <label className="block text-[13px] text-gray-700 mb-1">Fax No</label>
              <input type="text" name="fax_no" value={formData.fax_no} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={50} />
            </div>
          )}
          {formData.website !== undefined && (
            <div>
              <label className="block text-[13px] text-gray-700 mb-1">Website</label>
              <input type="text" name="website" value={formData.website} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={120} />
            </div>
          )}
          {formData.parent_company !== undefined && (
            <div>
              <label className="block text-[13px] text-gray-700 mb-1">Parent Company</label>
              <input type="text" name="parent_company" value={formData.parent_company} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={120} />
            </div>
          )}
          {formData.currency !== undefined && (
            <div>
              <label className="block text-[13px] text-gray-700 mb-1">Currency</label>
              <input type="text" name="currency" value={formData.currency} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={20} />
            </div>
          )}
          {formData.account_no !== undefined && (
            <div>
              <label className="block text-[13px] text-gray-700 mb-1">Account No</label>
              <input type="text" name="account_no" value={formData.account_no} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={50} />
            </div>
          )}
          {formData.swift_code !== undefined && (
            <div>
              <label className="block text-[13px] text-gray-700 mb-1">SWIFT Code</label>
              <input type="text" name="swift_code" value={formData.swift_code} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={50} />
            </div>
          )}
          {formData.ad_code !== undefined && (
            <div>
              <label className="block text-[13px] text-gray-700 mb-1">AD Code</label>
              <input type="text" name="ad_code" value={formData.ad_code} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={30} />
            </div>
          )}
          {formData.iec_code !== undefined && (
            <div>
              <label className="block text-[13px] text-gray-700 mb-1">IEC Code</label>
              <input type="text" name="iec_code" value={formData.iec_code} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={30} />
            </div>
          )}
          {formData.gst_no !== undefined && (
            <div>
              <label className="block text-[13px] text-gray-700 mb-1">GST No</label>
              <input type="text" name="gst_no" value={formData.gst_no} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={30} />
            </div>
          )}
          {formData.fssai_no !== undefined && (
            <div>
              <label className="block text-[13px] text-gray-700 mb-1">FSSAI No</label>
              <input type="text" name="fssai_no" value={formData.fssai_no} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={30} />
            </div>
          )}
          {formData.pan_no !== undefined && (
            <div>
              <label className="block text-[13px] text-gray-700 mb-1">PAN No</label>
              <input type="text" name="pan_no" value={formData.pan_no} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={100} />
            </div>
          )}
          {formData.pincode !== undefined && (
            <div>
              <label className="block text-[13px] text-gray-700 mb-1">Pincode</label>
              <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={45} />
            </div>
          )}
                     {formData.address !== undefined && (
             <div>
               <label className="block text-[13px] text-gray-700 mb-1">Address</label>
               <input type="text" name="address" value={formData.address} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={255} />
             </div>
           )}
           {formData.state !== undefined && (
             <div>
               <label className="block text-[13px] text-gray-700 mb-1">State</label>
               <select
                 name="state"
                 value={formData.state}
                 onChange={handleStateChange}
                 onBlur={handleBlur}
                 className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
               >
                 <option value="">-- Select State --</option>
                 {stateList.map((s, i) => (
                   <option key={i} value={s.name}>{s.name}</option>
                 ))}
               </select>
             </div>
           )}
           {formData.state_code !== undefined && (
             <div>
               <label className="block text-[13px] text-gray-700 mb-1">State Code</label>
               <input
                 type="text"
                 name="state_code"
                 value={formData.state_code}
                 readOnly
                 className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-gray-100 focus:outline-none rounded-none"
                 maxLength={10}
               />
             </div>
           )}
         </div>
        <div className="pt-2 flex items-center gap-3">
          <button type="submit" className="px-3 py-1.5 text-[12px] bg-[#15803d] text-white font-normal rounded border border-[#15803d] hover:bg-[#166534] transition disabled:opacity-60" style={{ minWidth: '110px' }} disabled={loading}>
            {loading ? 'Updating...' : 'Update Supplier'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SupplierEditForm; 